/* eslint-disable react/no-array-index-key */
import Link from 'components/Globals/Base/Link';
import Heading from 'styles/typography/Heading';
import { ARTICLE_PAGE_TEST_IDS } from 'constants/testsIds/articlePage';
import ProgramCard from 'components/ProgramCard';
import type { EduAdWigetsByPosition, EduInstitution } from 'interfaces/EduRanking';
import EDUCATION_ADS_TEST_IDS from 'constants/testsIds/educationAds';
import dynamic from 'next/dynamic';
import * as S from './SchoolPageStyles';

export interface SchoolPageProps {
  institution: EduInstitution;
  adWidgets: EduAdWigetsByPosition;
}

const Breadcrumbs = dynamic(() => import('components/Breadcrumbs'));
const PaidPlacement = dynamic(() => import('components/PaidPlacement'));

const SchoolPage = ({ institution, adWidgets }: SchoolPageProps) => {
  const { name, ctaUrl, programs, city, state, breadcrumbs } = institution;
  const showBreadcrumbs = breadcrumbs?.items && breadcrumbs.items.length > 0;

  return (
    <S.Wrapper>
      <S.Banner data-cy={ARTICLE_PAGE_TEST_IDS.SCHOOL_PAGE_BANNER}>
        {showBreadcrumbs && (
          <Breadcrumbs
            items={breadcrumbs.items}
            hasBrandColor
          />
        )}
        <Heading
          data-cy={ARTICLE_PAGE_TEST_IDS.SCHOOL_PAGE_TITLE}
          $fontFamily='saol'
          $size={{ default: 'level3', lgMin: 'level1', mdMin: 'level2', xlMin: 'large' }}
        >
          {name}
        </Heading>
        {city && (
          <address data-cy={ARTICLE_PAGE_TEST_IDS.SCHOOL_PAGE_ADDRESS}>{`${city}${
            state?.abbreviation ? `, ${state.abbreviation}` : ''
          }`}</address>
        )}
        {ctaUrl && (
          <Link
            data-cy={ARTICLE_PAGE_TEST_IDS.SCHOOL_PAGE_LINK}
            type='primaryBrandButton'
            href={ctaUrl}
            target='_blank'
            rel='nofollow noopener noreferrer'
          >
            VISIT SCHOOL WEBSITE
          </Link>
        )}
      </S.Banner>

      <S.PageContent>
        {programs?.map(
          ({ name: programName, rankingName, rankingShortTitle, rankingPath, description, properties, year }) => (
            <ProgramCard
              key={programName}
              cta={{
                text: `See full ${rankingName} for ${year}`,
                url: rankingPath,
              }}
              description={description}
              table={properties}
              title={rankingShortTitle || rankingName}
            />
          ),
        )}
        {adWidgets && adWidgets.in_stream && adWidgets.in_stream[0] && (
          <PaidPlacement
            dataCy={EDUCATION_ADS_TEST_IDS.SCHOOL_INSTREAM}
            adContent={adWidgets.in_stream[0].content}
            adTag={adWidgets.in_stream[0].adTag}
            position='in_stream'
            utmCampaign='schools'
            entityId={institution.prefixedDatabaseId}
          />
        )}
      </S.PageContent>

      <S.RightRail data-cy={ARTICLE_PAGE_TEST_IDS.SCHOOL_PAGE_RIGHT_RAIL}>
        {adWidgets && adWidgets.right_side && adWidgets.right_side[0] && (
          <PaidPlacement
            dataCy={EDUCATION_ADS_TEST_IDS.SCHOOL_RAIL}
            adContent={adWidgets.right_side[0].content}
            adTag={adWidgets.right_side[0].adTag}
            position='right_side'
            utmCampaign='schools'
            entityId={institution.prefixedDatabaseId}
          />
        )}
      </S.RightRail>
    </S.Wrapper>
  );
};

export default SchoolPage;
